<script>
import { mapState } from "vuex";
import { catalogoBusca, buscar, sugestoes } from "@/utils/busca-sistema";

/**
 * Busca global ("paleta de comandos") da barra superior.
 *
 * Abre pelo campo da barra, por Ctrl+K / ⌘K ou pela tecla "/" (fora de campos
 * de texto). Só lista o que o usuário já enxerga no menu — a regra de
 * visibilidade é a do utils/menu-map.js, via utils/busca-sistema.js.
 *
 * Emite:
 *   tutorial  → a barra superior reabre o tour ("Como funciona")
 *   tema      → a barra superior alterna claro/escuro
 */
export default {
  name: "GlobalSearch",
  data() {
    return {
      aberto: false,
      consulta: "",
      ativo: 0,
    };
  },
  computed: {
    ...mapState({ account: (state) => state.account }),
    catalogo() {
      return catalogoBusca(this.account.user);
    },
    resultados() {
      return this.consulta.trim() ? buscar(this.catalogo, this.consulta) : sugestoes(this.catalogo);
    },
    atalho() {
      return /Mac|iPhone|iPad/.test(navigator.platform || "") ? "⌘ K" : "Ctrl K";
    },
  },
  watch: {
    consulta() {
      this.ativo = 0;
    },
    $route() {
      this.fechar();
    },
  },
  mounted() {
    document.addEventListener("keydown", this.onTeclaGlobal);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.onTeclaGlobal);
    document.documentElement.classList.remove("st-busca-aberta");
  },
  methods: {
    onTeclaGlobal(e) {
      const k = (e.key || "").toLowerCase();
      if ((e.ctrlKey || e.metaKey) && k === "k") {
        e.preventDefault();
        this.aberto ? this.fechar() : this.abrir();
        return;
      }
      if (k === "/" && !this.aberto) {
        const alvo = e.target;
        const digitando = alvo && (alvo.isContentEditable || /^(input|textarea|select)$/i.test(alvo.tagName));
        if (!digitando) {
          e.preventDefault();
          this.abrir();
        }
      }
    },
    abrir() {
      this.aberto = true;
      this.consulta = "";
      this.ativo = 0;
      this.focoAnterior = document.activeElement;
      document.documentElement.classList.add("st-busca-aberta");
      this.$nextTick(() => this.$refs.campo && this.$refs.campo.focus());
    },
    fechar() {
      if (!this.aberto) return;
      this.aberto = false;
      document.documentElement.classList.remove("st-busca-aberta");
      if (this.focoAnterior && this.focoAnterior.focus) this.focoAnterior.focus();
    },
    mover(delta) {
      const n = this.resultados.length;
      if (!n) return;
      this.ativo = (this.ativo + delta + n) % n;
      this.$nextTick(() => {
        const el = this.$el.querySelector(".st-search-item.is-active");
        if (el) el.scrollIntoView({ block: "nearest" });
      });
    },
    escolher(item) {
      if (!item) return;
      this.fechar();

      if (item.action) {
        this.$emit(item.action);
        return;
      }

      if (item.menuKey) {
        // Itens externos (CRM, BI Teams, Comunidade): o próprio link do menu
        // lateral já sabe fazer o SSO; repassamos o clique para ele.
        const link = document.querySelector(`[data-tour="menu-${item.menuKey}"] > a`);
        if (link) link.click();
        return;
      }

      if (item.path && item.path !== this.$route.path) this.$router.push(item.path);
    },
  },
};
</script>

<template>
  <div class="st-search">
    <!-- Gatilho: parece um campo, mas abre a paleta (no celular vira só o ícone). -->
    <button type="button" class="st-search-trigger" aria-haspopup="dialog" :aria-expanded="aberto ? 'true' : 'false'"
      aria-label="Buscar no sistema" @click="abrir">
      <i class="bx bx-search" aria-hidden="true"></i>
      <span class="st-search-trigger-text">Buscar no sistema…</span>
      <kbd class="st-kbd">{{ atalho }}</kbd>
    </button>

    <transition name="st-search-fade">
      <div v-if="aberto" class="st-search-overlay" @mousedown.self="fechar">
        <div class="st-search-panel" role="dialog" aria-modal="true" aria-label="Buscar no sistema">
          <div class="st-search-input-row">
            <i class="bx bx-search" aria-hidden="true"></i>
            <input ref="campo" v-model="consulta" type="text" class="st-search-input"
              placeholder="O que você procura? Ex.: adesões, metas, senha…" autocomplete="off" spellcheck="false"
              role="combobox" aria-autocomplete="list" aria-controls="st-search-lista"
              :aria-activedescendant="resultados.length ? 'st-search-op-' + ativo : null"
              @keydown.down.prevent="mover(1)" @keydown.up.prevent="mover(-1)"
              @keydown.enter.prevent="escolher(resultados[ativo])" @keydown.esc.prevent="fechar" />
            <button type="button" class="st-kbd st-search-esc" aria-label="Fechar busca" @click="fechar">Esc</button>
          </div>

          <div class="st-search-body">
            <p class="st-search-section">{{ consulta.trim() ? "Resultados" : "Sugestões" }}</p>
            <ul v-if="resultados.length" id="st-search-lista" class="st-search-list" role="listbox">
              <li v-for="(item, i) in resultados" :id="'st-search-op-' + i" :key="item.id" role="option"
                :aria-selected="i === ativo ? 'true' : 'false'" class="st-search-item" :class="{ 'is-active': i === ativo }"
                @mousemove="ativo = i" @click="escolher(item)">
                <span class="st-search-item-icon" aria-hidden="true"><i class="bx" :class="item.icon"></i></span>
                <span class="st-search-item-text">
                  <span class="st-search-item-label">{{ item.label }}</span>
                  <span class="st-search-item-context">{{ item.context }}</span>
                </span>
                <i class="bx bx-subdirectory-left st-search-item-enter" aria-hidden="true"></i>
              </li>
            </ul>
            <div v-else class="st-search-empty">
              <i class="bx bx-search-alt" aria-hidden="true"></i>
              <p>Nada encontrado para “{{ consulta }}”.</p>
              <small>A busca mostra só as telas liberadas para o seu perfil.</small>
            </div>
          </div>

          <div class="st-search-footer" aria-hidden="true">
            <span><kbd class="st-kbd">↑</kbd><kbd class="st-kbd">↓</kbd> navegar</span>
            <span><kbd class="st-kbd">Enter</kbd> abrir</span>
            <span><kbd class="st-kbd">Esc</kbd> fechar</span>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>
